import React from "react";

import { Building2, Settings, Ticket } from "lucide-react";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import { TypeEnum } from "../../../api/ProjectAPI.types";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  countColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../dataTable/dataTableStyles";
import { PROJECTS_ROUTE } from "../../routes/routes";

import type { TFunction } from "i18next";

import type { ProjectRow } from "./projectColumns.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

interface ProjectColumnsOptions {
  t: TFunction;
  onEdit: (project: ProjectRow) => void;
}

export function getProjectHref(name: string): string {
  return name ? `${PROJECTS_ROUTE}/${name}/dashboard` : PROJECTS_ROUTE;
}

export function getProjectEditHref(name: string): string {
  return `${PROJECTS_ROUTE}/${name}/edit`;
}

export function projectColumns({
  t,
  onEdit,
}: ProjectColumnsOptions): DataTableColumnDef<ProjectRow>[] {
  const status = statusColumn<ProjectRow>("status", t("status"), [
    { value: "active", label: t("common:active"), tone: "success" },
    { value: "stop", label: t("common:stop"), tone: "error" },
  ]);

  return [
    { ...status, meta: { ...status.meta, filterOptions: undefined } },
    {
      id: "type",
      accessorFn: (project) => project.type,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("type")} />
      ),
      enableSorting: false,
      meta: { label: t("type"), width: "6%", minWidth: 50, align: "center" },
      cell: ({ row }) => {
        const isEdu = row.original.type === TypeEnum.Edu;
        const Icon = isEdu ? Building2 : Ticket;
        const label = t(isEdu ? "edus" : "standard");

        return (
          <span
            className="inline-flex"
            data-tooltip-content={label}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <Icon aria-hidden="true" className={CELL_ICON_CLASS} />
            <span className="sr-only">{label}</span>
          </span>
        );
      },
    },
    {
      id: "name",
      accessorFn: (project) => project.name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("projectName")} />
      ),
      meta: { label: t("projectName") },
      cell: ({ row }) => (
        <DataTableLinkCell href={getProjectHref(row.original.name)}>
          {row.original.name}
        </DataTableLinkCell>
      ),
    },
    countColumn<ProjectRow>("active_offers", t("activeOffers"), undefined, {
      width: "12%",
      minWidth: 145,
    }),
    countColumn<ProjectRow>(
      "registered_users",
      t("registeredUsers"),
      undefined,
      {
        width: "16%",
        minWidth: 200,
      },
    ),
    countColumn<ProjectRow>("active_users", t("activeUsers"), undefined, {
      width: "12%",
      minWidth: 150,
    }),
    countColumn<ProjectRow>(
      "trialing_users",
      t("editor-analytics:trialing_users"),
      undefined,
      {
        width: "12%",
        minWidth: 145,
      },
    ),
    countColumn<ProjectRow>("churn", t("churn"), undefined, {
      width: "12%",
      minWidth: 150,
    }),
    actionsColumn<ProjectRow>(
      (project) => [
        {
          // Mène aux paramètres du projet ; la ligne, elle, ouvre son tableau de bord.
          label: t("editor-menu:settings"),
          icon: <Settings />,
          hidden: !project.canEdit,
          onClick: () => onEdit(project),
        },
      ],
      (project) => project.name,
    ),
  ];
}
